import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { FormattedMessage } from '../../util/reactIntl';
import { featuredPriceValues } from '../../util/featured';

import useHelloVideoMap from '../HelloVideo/useHelloVideoMap';
import css from './HowToGetBooked.module.css';

// Earners Playbook (Sam 2026-10-31). Checklist with progress.
// Auto-checks from live profile signals where we have them; the rest the earner
// ticks themselves (saved per user in this browser).
const STEPS = [
  { id: 'photos', section: 'Profile', msg: 'HowToGetBooked.profile', href: '/profile-settings' },
  { id: 'helloVideo', section: 'Profile', msg: 'HowToGetBooked.helloVideo', href: '/profile-settings' },
  { id: 'wording', section: 'Profile', msg: 'HowToGetBooked.wording', href: '/profile-settings' },
  { id: 'topics', section: 'Profile', msg: 'HowToGetBooked.topics', href: '/profile-settings' },
  { id: 'notifications', section: 'Profile', msg: 'HowToGetBooked.notifications', href: '/account/contact-details' },
  { id: 'calendar', section: 'Profile', msg: 'HowToGetBooked.availableNow' },
  { id: 'practice', section: 'Get found', msg: 'HowToGetBooked.practice', href: '/s' },
  { id: 'trending', section: 'Get found', msg: 'HowToGetBooked.trending', href: '/profile-settings' },
  { id: 'promote', section: 'Get found', msg: 'HowToGetBooked.promote', href: '/promote' },
  { id: 'friends', section: 'Get found', msg: 'HowToGetBooked.friends', href: '/promote' },
  { id: 'gratuities', section: 'Make money', msg: 'HowToGetBooked.gratuities' },
  { id: 'earnings', section: 'Make money', msg: 'HowToGetBooked.earnings' },
  { id: 'aiCoach', section: 'Make money', msg: 'HowToGetBooked.aiCoach' },
];
const MORE_IDS = [
  'HowToGetBooked.showUp',
  'HowToGetBooked.reviews',
  'HowToGetBooked.formats',
  'HowToGetBooked.availability',
];

const storageKey = uid => `sl.earnerPlaybook.v1.${uid || 'anon'}`;

const autoDone = user => {
  const profile = user?.attributes?.profile || {};
  const bio = (profile.bio || '').trim();
  const hasPhoto = !!user?.profileImage?.id;
  return {
    photos: hasPhoto ? undefined : false, // photo present still needs "more" → manual tick
    wording: bio.length >= 160 ? true : undefined,
  };
};

export const PLAYBOOK_TOTAL = STEPS.length;
export const usePlaybookProgress = () => {
  const currentUser = useSelector(st => st?.user?.currentUser || null);
  const uid = currentUser?.id?.uuid;
  const [manual, setManual] = useState({});

  useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' && window.localStorage.getItem(storageKey(uid));
      setManual(raw ? JSON.parse(raw) : {});
    } catch (e) {
      setManual({});
    }
  }, [uid]);

  const helloMap = useHelloVideoMap(uid ? [uid] : []);
  const auto = { ...autoDone(currentUser), ...(uid && helloMap[uid] ? { helloVideo: true } : {}) };
  const isDone = id => (auto[id] === true ? true : !!manual[id]);
  const toggle = id => {
    if (auto[id] === true) return;
    const next = { ...manual, [id]: !manual[id] };
    setManual(next);
    try {
      window.localStorage.setItem(storageKey(uid), JSON.stringify(next));
    } catch (e) {
      // ignore
    }
  };
  const doneCount = STEPS.filter(s => isDone(s.id)).length;
  const pct = Math.round((doneCount / STEPS.length) * 100);
  return { doneCount, pct, total: STEPS.length, isDone, toggle, auto };
};

const HowToGetBooked = props => {
  const {
    className,
    headingAs = 'h2',
    headingId = 'how-to-get-booked',
    compact = false,
    flush = false,
    pinkChecks = false,
    blueChecks = false,
    hideTitle = false,
    sections = false,
  } = props;
  const HeadingTag = headingAs;
  const prices = featuredPriceValues();
  const { doneCount, pct, isDone, toggle, auto } = usePlaybookProgress();

  return (
    <aside
      className={classNames(
        css.root,
        { [css.compact]: compact, [css.flush]: flush, [css.pinkChecks]: pinkChecks, [css.blueChecks]: blueChecks },
        className
      )}
      aria-labelledby={headingId}
    >
      {hideTitle ? null : (
        <HeadingTag id={headingId} className={css.title}>
          <FormattedMessage id="HowToGetBooked.title" />
        </HeadingTag>
      )}
      <p className={css.intro}>
        <FormattedMessage id="HowToGetBooked.intro" />
      </p>
      <div className={css.progressRow}>
        <span className={css.progressLabel}>
          <FormattedMessage id="HowToGetBooked.progress" values={{ done: doneCount, total: STEPS.length }} />
        </span>
        <div className={css.progressBar} role="progressbar" aria-valuemin={0} aria-valuemax={STEPS.length} aria-valuenow={doneCount}>
          <div className={css.progressFill} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <ol className={classNames(css.checklist, { [css.chapters]: sections })}>
        {STEPS.map((s, i) => {
          const done = isDone(s.id);
          const locked = auto[s.id] === true;
          const header =
            sections && (i === 0 || STEPS[i - 1].section !== s.section) ? (
              <li key={`sec-${s.section}`} className={css.sectionHead} aria-hidden="false">
                <span className={css.sectionName}>{s.section}</span>
                <span className={css.sectionCount}>
                  {STEPS.filter(x => x.section === s.section && isDone(x.id)).length} /{' '}
                  {STEPS.filter(x => x.section === s.section).length}
                </span>
              </li>
            ) : null;
          return (
            <React.Fragment key={s.id}>
            {header}
            <li className={classNames(css.step, { [css.stepDone]: done })}>
              <button
                type="button"
                className={css.check}
                aria-pressed={done}
                aria-label={done ? 'Mark not done' : 'Mark done'}
                onClick={() => toggle(s.id)}
                disabled={locked}
              >
                {done ? '✓' : i + 1}
              </button>
              <div className={css.stepBody}>
                {sections && done ? <span className={css.chapterTag}>Nailed it 🎉</span> : null}
                <FormattedMessage id={s.msg} />
                {s.href ? (
                  <a className={css.stepLink} href={s.href}>
                    <FormattedMessage id="HowToGetBooked.go" />
                  </a>
                ) : null}
                {locked ? (
                  <span className={css.autoTag}>
                    <FormattedMessage id="HowToGetBooked.autoDone" />
                  </span>
                ) : null}
              </div>
            </li>
            </React.Fragment>
          );
        })}
      </ol>
      <h3 className={css.moreTitle}>
        <FormattedMessage id="HowToGetBooked.moreTitle" />
      </h3>
      <ul className={css.list}>
        {MORE_IDS.map(id => (
          <li key={id} className={css.item}>
            <FormattedMessage id={id} />
          </li>
        ))}
      </ul>
      <p className={css.featured}>
        <FormattedMessage id="HowToGetBooked.featured" values={prices} />
      </p>
      <p className={css.feedback}>
        <FormattedMessage id="HowToGetBooked.feedback" />{' '}
        <a href="/contact" className={css.stepLink}>
          <FormattedMessage id="HowToGetBooked.feedbackCta" />
        </a>
      </p>
    </aside>
  );
};

export default HowToGetBooked;

// Status card + link to the dedicated /earners-playbook page (dashboard + Tips).
export const EarnersPlaybookStatus = ({ className, placement }) => {
  const { doneCount, pct, total } = usePlaybookProgress();
  const allDone = doneCount >= total;
  // Sam 11/1: incomplete -> pinned top of dashboard; complete -> small link lower down.
  if (placement === 'top' && allDone) return null;
  if (placement === 'bottom' && !allDone) return null;
  if (placement === 'bottom' && allDone) {
    return (
      <a href="/earners-playbook" className={className} data-earners-playbook-status={`${doneCount}/${total}`} style={{ display: 'block', fontSize: 14, margin: '8px 0' }}>
        📘 Earners Playbook complete ✓ — open
      </a>
    );
  }
  return (
    <a
      href="/earners-playbook"
      className={classNames(css.statusCard, className)}
      data-earners-playbook-status={`${doneCount}/${total}`}
    >
      <span className={css.statusIcon} aria-hidden="true">📘</span>
      <span className={css.statusBody}>
        <span className={css.statusTitle}>Earners Playbook</span>
        <span className={css.statusLine}>
          {allDone
            ? `All ${total} done — you're a pro. Keep reeling them in! 💸`
            : `${doneCount} of ${total} done · we bring the crowd, you reel them in`}
        </span>
        <span className={css.progressBar} role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={doneCount}>
          <span className={css.progressFill} style={{ width: `${pct}%`, display: 'block' }} />
        </span>
      </span>
      <span className={css.statusCta}>{doneCount === 0 ? 'Start →' : allDone ? 'Open →' : 'Keep going →'}</span>
    </a>
  );
};
